import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Building2, Users, ArrowRight, Shield, CheckCircle, UserCog, Globe, Mail } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

export default function RegisterChoicePage() {
  const { t, language, setLanguage, languages } = useLanguage();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F5F0E8] to-[#E8E0D5] dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="w-full max-w-5xl">
        {/* Language Switcher */}
        <div className="flex justify-end mb-4">
          <Select value={language} onValueChange={setLanguage}>
            <SelectTrigger className="w-[140px] bg-white/80 backdrop-blur">
              <Globe className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {languages.map((lang) => (
                <SelectItem key={lang.code} value={lang.code}>
                  <span className="flex items-center gap-2">
                    <span>{lang.flag}</span>
                    <span>{lang.nativeName}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <img src="/logo.png" alt="U-DORM" className="w-14 h-14 object-contain" />
            <span className="text-2xl font-bold text-[#2D3A3A] dark:text-white">U-DORM</span>
          </Link>
          <h1 className="text-3xl font-bold text-[#2D3A3A] dark:text-white mb-2">
            {t('auth.createAccount')}
          </h1>
          <p className="text-muted-foreground">
            {t('common.selectOption')}
          </p>
        </div>

        {/* Role Selection Cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {/* Landlord Card */}
          <Card className="relative overflow-hidden hover:shadow-lg transition-shadow border-2 hover:border-[#C85A42]" data-testid="register-landlord-card">
            <CardHeader className="pb-4">
              <div className="w-14 h-14 rounded-xl bg-[#C85A42]/10 flex items-center justify-center mb-4">
                <Building2 className="w-7 h-7 text-[#C85A42]" />
              </div>
              <CardTitle className="text-xl">{t('landing.forLandlords')}</CardTitle>
              <CardDescription>
                {t('nav.properties')} & {t('payments.title').toLowerCase()}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>{t('properties.addProperty')}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>{t('leases.createLease')}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>{t('payments.title')} & {t('nav.expenses')}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>{t('tenants.inviteTenant')}</span>
                </div>
              </div>
              
              <div className="pt-2">
                <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950 p-2 rounded mb-3">
                  <Shield className="w-4 h-4" />
                  <span>{t('dashboard.pendingVerification')}</span>
                </div>
                <Button asChild className="w-full bg-[#C85A42] hover:bg-[#A84832]" data-testid="register-as-landlord-btn">
                  <Link to="/register/landlord">
                    {t('auth.register')} {t('landing.forLandlords')}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Property Manager Card - Invitation Only */}
          <Card className="relative overflow-hidden border-2 border-dashed opacity-80" data-testid="register-manager-card">
            <CardHeader className="pb-4">
              <div className="w-14 h-14 rounded-xl bg-[#5B7B7B]/10 flex items-center justify-center mb-4">
                <UserCog className="w-7 h-7 text-[#5B7B7B]" />
              </div>
              <CardTitle className="text-xl">{t('landing.forManagers')}</CardTitle>
              <CardDescription>
                {t('nav.properties')} & {t('nav.tenants').toLowerCase()}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>{t('nav.properties')}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>{t('nav.tenants')}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>{t('payments.title')}</span>
                </div>
              </div>
              
              <div className="pt-2">
                <div className="flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950 p-3 rounded">
                  <Mail className="w-4 h-4 shrink-0" />
                  <span>By invitation only. Ask your landlord to send you an invite.</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tenant Card */}
          <Card className="relative overflow-hidden hover:shadow-lg transition-shadow border-2 hover:border-[#6B8E6B]" data-testid="register-tenant-card">
            <CardHeader className="pb-4">
              <div className="w-14 h-14 rounded-xl bg-[#6B8E6B]/10 flex items-center justify-center mb-4">
                <Users className="w-7 h-7 text-[#6B8E6B]" />
              </div>
              <CardTitle className="text-xl">{t('landing.forTenants')}</CardTitle>
              <CardDescription>
                {t('nav.leases')} & {t('payments.title').toLowerCase()}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>{t('leases.viewLease')}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>{t('payments.submitPayment')}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>{t('payments.downloadReceipt')}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>{t('nav.messages')}</span>
                </div>
              </div>
              
              <div className="pt-2">
                <div className="flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950 p-2 rounded mb-3">
                  <Shield className="w-4 h-4" />
                  <span>{t('common.required')}: {t('auth.landlordCode')}</span>
                </div>
                <Button asChild variant="outline" className="w-full border-[#6B8E6B] text-[#6B8E6B] hover:bg-[#6B8E6B] hover:text-white" data-testid="register-as-tenant-btn">
                  <Link to="/register/tenant">
                    {t('auth.register')} {t('landing.forTenants')}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-sm text-muted-foreground">
            {t('auth.alreadyHaveAccount')}{' '}
            <Link to="/login" className="text-[#C85A42] hover:underline font-medium">
              {t('auth.signIn')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
